import s from './TourAudience.module.scss';
import { CLOUDINARY_BASE_URL } from '../../config/cloudinary';
import { useLanguage } from '../../contexts/LanguageContext';

const img = (path) => `${CLOUDINARY_BASE_URL}${path}`;

const BASE_ITEMS = [
  {
    filterKey: 'general',
    icon: 'person',
    image: img('v1782876545/06_euvqzf_ebiz6b.webp'),
  },
  {
    filterKey: 'influencer',
    icon: 'star',
    image: img('v1782876545/1_vohkmm_x6vtrq.webp'),
  },
  {
    filterKey: 'creator',
    icon: 'camera',
    image: img('v1782876547/DJI_20260619124507_0025_D_a5qe1a.webp'),
  },
];

const TEXT = {
  JP: {
    lead: 'あなたに合ったツアーを探す',
    items: {
      general: {
        title: '一般の方',
        text: '一人旅の記念にも、友人同士の思い出づくりにも。特別な瞬間を写真と映像で残します。',
        alt: '一般の方向け宮古島ツアー',
      },
      influencer: {
        title: 'インフルエンサーの方',
        text: '撮影に最高品質のクオリティが欲しい方へ。撮影前の打ち合わせや絵コンテ作成、一般開放していない時間のカフェやヴィラでの撮影も可能です。',
        alt: 'インフルエンサー向け宮古島撮影ツアー',
      },
      creator: {
        title: 'クリエイターの方',
        text: '映像・写真制作に最適なロケーションをご提案し、効率よくご案内します。モデルの手配もご相談可能です。',
        alt: 'クリエイター向け宮古島ロケーションツアー',
      },
    },
  },

  EN: {
    lead: 'Find the perfect tour for you',
    items: {
      general: {
        title: 'General Guests',
        text: 'Perfect for solo travelers, friends, and special memories. We capture your unforgettable moments through photos and videos.',
        alt: 'Miyakoisland tour for general guests',
      },
      influencer: {
        title: 'Influencers',
        text: 'For those who want premium-quality content. We can arrange pre-shoot planning, storyboards, and shoots at cafes or villas during special private hours.',
        alt: 'Miyakoisland photo tour for influencers',
      },
      creator: {
        title: 'Creators',
        text: 'We suggest the best locations for photo and video production and guide you efficiently. Model arrangements are also available upon request.',
        alt: 'Miyakoisland location tour for creators',
      },
    },
  },

  中文: {
    lead: '寻找适合你的行程',
    items: {
      general: {
        title: '普通游客',
        text: '无论是一个人的旅行纪念，还是和朋友一起创造回忆，我们都会用照片和视频记录你的特别瞬间。',
        alt: '适合普通游客的宫古岛旅行',
      },
      influencer: {
        title: '网红 / 博主',
        text: '适合希望获得高品质拍摄内容的客人。我们可以提前沟通拍摄方案、制作分镜，也可安排咖啡馆或别墅的特殊时段拍摄。',
        alt: '适合网红的宫古岛拍摄旅行',
      },
      creator: {
        title: '内容创作者',
        text: '我们会推荐适合影像和照片制作的最佳地点，并高效带你前往。也可以咨询模特安排。',
        alt: '适合内容创作者的宫古岛外景旅行',
      },
    },
  },

  한국어: {
    lead: '나에게 맞는 투어 찾기',
    items: {
      general: {
        title: '일반 고객',
        text: '혼자 여행의 기념으로도, 친구들과의 추억 만들기로도 좋습니다. 특별한 순간을 사진과 영상으로 남겨드립니다.',
        alt: '일반 고객을 위한 미야코지마 투어',
      },
      influencer: {
        title: '인플루언서',
        text: '최고 퀄리티의 촬영 콘텐츠를 원하는 분께 추천합니다. 촬영 전 미팅, 콘티 제작, 일반 운영 시간 외 카페나 빌라 촬영도 가능합니다.',
        alt: '인플루언서를 위한 미야코지마 촬영 투어',
      },
      creator: {
        title: '크리에이터',
        text: '영상과 사진 제작에 적합한 로케이션을 제안하고 효율적으로 안내합니다. 모델 섭외도 상담 가능합니다.',
        alt: '크리에이터를 위한 미야코지마 로케이션 투어',
      },
    },
  },
};

const AudienceIcon = ({ type }) => {
  if (type === 'star') {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M12 3.5l2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 17.4l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3.5z" />
      </svg>
    );
  }

  if (type === 'camera') {
    return (
      <svg viewBox="0 0 24 24">
        <rect x="4" y="7" width="16" height="12" rx="3" />
        <path d="M8.5 7l1.7-2h3.6l1.7 2" />
        <circle cx="12" cy="13" r="3.2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c.8-4.2 3.2-6.5 7-6.5s6.2 2.3 7 6.5" />
    </svg>
  );
};

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const TourAudience = ({ activeFilter = 'all', onSelectFilter }) => {
  const { lang } = useLanguage();
  const t = TEXT[lang] || TEXT.JP;

  const handleSelect = (filterKey) => {
    onSelectFilter?.(filterKey);

    window.requestAnimationFrame(() => {
      document.getElementById('tour-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  return (
    <section className={s.root}>
      <p className={s.lead}>{t.lead}</p>

      <div className={s.grid}>
        {BASE_ITEMS.map((item) => {
          const content = t.items[item.filterKey] || TEXT.JP.items[item.filterKey];

          return (
            <button
              type="button"
              className={`${s.card} ${
                activeFilter === item.filterKey ? s.active : ''
              }`}
              key={item.filterKey}
              onClick={() => handleSelect(item.filterKey)}
            >
              <div className={s.icon}>
                <AudienceIcon type={item.icon} />
              </div>

              <div className={s.text}>
                <h3>{content.title}</h3>
                <p>{content.text}</p>
              </div>

              <div className={s.image}>
                <img src={item.image} alt={content.alt} />
              </div>

              <span className={s.arrow}>
                <ArrowIcon />
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default TourAudience;
